import type { DetalheGmud } from '../../../types/gmud'

/** O que o código precisa da solicitação: quem ela é e de quando é a versão impressa. */
type Identidade = Pick<DetalheGmud, 'codigo' | 'criadoEm' | 'atualizadoEm'>

/**
 * O "Código do Documento" que sai no rodapé de todas as páginas.
 *
 * No sistema anterior isto era `uniqid()` chamado dentro do `download()` do controller: um
 * valor aleatório por emissão. Duas impressões da MESMA solicitação, sem nada ter mudado
 * entre elas, saíam com códigos diferentes — o que torna o código inútil justamente para o
 * que um código de documento serve: dizer se a folha que está no processo é a mesma que
 * está na tela, ou uma versão anterior que alguém imprimiu antes de o plano mudar.
 *
 * Aqui ele é derivado do registro: código da solicitação mais um resumo do instante da
 * última alteração. Consequências, que são o motivo da mudança:
 *
 * - reimprimir a mesma versão dá exatamente o mesmo código, então duas cópias iguais se
 *   reconhecem como iguais;
 * - editar o plano muda `atualizadoEm` e muda o código, então a folha velha se denuncia
 *   sozinha ao lado da nova.
 *
 * É uma divergência deliberada em relação ao documento antigo, e a única do rodapé. O
 * resto da linha — chamado, data da emissão, "Página N de 4" — é o texto de lá.
 *
 * `atualizadoEm` é nulo enquanto ninguém tocou no registro depois de criá-lo (a API omite
 * campo vazio), e aí quem serve de versão é `criadoEm`.
 *
 * @param gmud a solicitação que está sendo impressa
 * @returns o código estável do documento, no formato `GMUD-2026-00042-1A2B3C4`
 */
export function codigoDoDocumento(gmud: Identidade): string {
  const versao = gmud.atualizadoEm ?? gmud.criadoEm
  return `${gmud.codigo}-${resumo(`${gmud.codigo}|${versao}`)}`
}

/**
 * Resumo curto e determinístico (djb2).
 *
 * Não é hash criptográfico e não precisa ser: o código do documento identifica versão, não
 * autentica nada. O que se pede dele é caber no rodapé, ser sempre o mesmo para a mesma
 * entrada e mudar quando a entrada muda. `crypto.subtle` daria mais garantias e é assíncrono,
 * o que obrigaria a atrasar a renderização do documento por um dígito de rodapé.
 *
 * @param texto identidade e versão da solicitação, concatenadas
 * @returns sete caracteres em base 36 maiúscula
 */
function resumo(texto: string): string {
  let acumulado = 5381
  for (const letra of texto) {
    // O `^` volta a 32 bits a cada passo, então a multiplicação nunca sai da faixa em que
    // o número em ponto flutuante ainda é exato.
    acumulado = (acumulado * 33) ^ letra.charCodeAt(0)
  }
  return (acumulado >>> 0).toString(36).toUpperCase().padStart(7, '0')
}
